/**
 * Copyright (c) 2025-2026 eBUCIS, LLC. All rights reserved.
 *
 * Main Application Component - React Router setup and authentication handling
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from './stores/authStore'
import useThemeStore from './stores/themeStore'
import useBrandingStore from './stores/brandingStore'
import { useOrganizationStore } from './stores/organizationStore'
import { useFeatureFlag } from './hooks/useFeatureFlag'
import { useEffect, useState, useCallback, lazy, Suspense } from 'react'
import Layout from './components/Layout'
import Login from './pages/Login'
import RegisterRouter from './pages/RegisterRouter'
import SignupLanding from './pages/SignupLanding'
import RegisterStudent from './pages/RegisterStudent'
import RegisterGuardian from './pages/RegisterGuardian'
import GuardianApproval from './pages/GuardianApproval'
import WaiverApproval from './pages/WaiverApproval'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import EmailVerificationRequired from './pages/EmailVerificationRequired'
import AccountDisabled from './pages/AccountDisabled'
import VerifyEmail from './pages/VerifyEmail'
import WaitlistVerify from './pages/WaitlistVerify'
import Dashboard from './pages/Dashboard'
import CoachDashboardPage from './pages/CoachDashboardPage'
import ChallengeSessionV2 from './pages/ChallengeSessionV2'
import ChallengeComplete from './pages/ChallengeComplete'
import ChallengeReport from './pages/ChallengeReport'
import Profile from './pages/Profile'
import History from './pages/History'
import MyCoach from './pages/MyCoach'
import StrategyProgress from './pages/StrategyProgress'
import Rankings from './pages/Rankings'
import Progress from './pages/Progress'
import PublicProfile from './pages/PublicProfile'
import RankingsEmbed from './pages/RankingsEmbed'
import ShareLanding from './pages/ShareLanding'
import PublicLeaderboard from './pages/PublicLeaderboard'
import ChildDetailPage from './pages/ChildDetailPage'
import ClientDetailPage from './pages/ClientDetailPage'
import FocusedPracticeList from './pages/FocusedPracticeList'
import FocusedPracticeReview from './pages/FocusedPracticeReview'
import FocusedPracticePractice from './pages/FocusedPracticePractice'
import FocusedPracticeHomework from './pages/FocusedPracticeHomework'
import FocusedPracticeResults from './pages/FocusedPracticeResults'
import AdminDashboard from './pages/AdminDashboard'
import JoinReferral from './pages/JoinReferral'
import JoinEmailInvite from './pages/JoinEmailInvite'
import Referrals from './pages/Referrals'
import ChallengeTypeSelection from './pages/ChallengeTypeSelection'
import FeedbackDetail from './pages/FeedbackDetail'
import MyFeedback from './pages/MyFeedback'
import Messages from './pages/Messages'
import Games from './pages/Games'
import MathMatchingGame from './pages/MathMatchingGame'
import SpeedVocab from './pages/games/SpeedVocab'
import GrammarFix from './pages/games/GrammarFix'
import ContextClues from './pages/games/ContextClues'
import CheckoutPage from './pages/CheckoutPage'
import AcceptCoachInvitation from './pages/AcceptCoachInvitation'
import TermsOfService from './pages/legal/TermsOfService'
import PrivacyPolicy from './pages/legal/PrivacyPolicy'
import PrivacyRequest from './pages/PrivacyRequest'
import GettingStarted from './pages/help/GettingStarted'
import Contact from './pages/help/Contact'
import Help from './pages/help/Help'
import ConfirmDeletion from './pages/ConfirmDeletion'
import TermsReacceptanceModal from './components/compliance/TermsReacceptanceModal'
import LoadingSpinner from './components/LoadingSpinner'
import SessionExpiredModal from './components/SessionExpiredModal'
import InsufficientPrivilegesModal from './components/InsufficientPrivilegesModal'

import FeedbackModal from './components/feedback/FeedbackModal'
import FeedbackDetailModal from './components/feedback/FeedbackDetailModal'
import AdminFeedbackDetailModal from './components/feedback/AdminFeedbackDetailModal'
import WebSocketErrorBoundary from './components/WebSocketErrorBoundary'
import loggingService from './services/loggingService'
import openReplayService from './services/openReplayService'
import { saveLastPath } from './services/lastPathService'
import ServerConnectionBanner from './components/ServerConnectionBanner'
import toast from 'react-hot-toast'
import ImpersonationBanner from './components/ImpersonationBanner'
import { FomoNotificationContainer } from './components/fomo'
import useChallengeStore from './hooks/useChallenge'
import { useVersionCheck } from './hooks/useVersionCheck'
import { versionConfig } from './config/versionConfig'
import UpdateAvailableModal from './components/UpdateAvailableModal'
import WhatsNewModal from './components/WhatsNewModal'
import featureAnnouncementService from './services/featureAnnouncementService'
import ModalErrorBoundary from './components/ModalErrorBoundary'
import { ClassManagementGate } from './components/FeatureGate'

import { createLogger } from './utils/logger'

const logger = createLogger('App')

// Lazy-loaded class management components for code splitting
const ClassList = lazy(() => import('./components/class/ClassList'))
const ClassDetail = lazy(() => import('./components/class/ClassDetail'))
const JoinClass = lazy(() => import('./pages/JoinClass'))
// Lazy-loaded calculator for code splitting
const CalculatorPage = lazy(() => import('./pages/Calculator'))
// Lazy-loaded score reports and enhanced report for code splitting
const ScoreReports = lazy(() => import('./pages/ScoreReports'))
const ScoreReportDetailPage = lazy(() => import('./components/scoreReports/ScoreReportDetail'))
const MyReport = lazy(() => import('./pages/MyReport'))
// Lazy-loaded what's new page
const WhatsNewPage = lazy(() => import('./pages/WhatsNewPage'))
// Lazy-loaded calendar/scheduling for code splitting
const CalendarPage = lazy(() => import('./pages/Calendar'))
const EventDetailPage = lazy(() => import('./pages/EventDetail'))
const KnowledgeMap = lazy(() => import('./pages/KnowledgeMap'))
const EducatorKnowledgeMap = lazy(() => import('./pages/EducatorKnowledgeMap'))
const TutorPracticeList = lazy(() => import('./pages/TutorPracticeList'))
const TutorPracticeTeaching = lazy(() => import('./pages/TutorPracticeTeaching'))
const TutorPracticeResults = lazy(() => import('./pages/TutorPracticeResults'))
const TutorAssignedPractices = lazy(() => import('./pages/TutorAssignedPractices'))
const AdaptiveTeaching = lazy(() => import('./pages/AdaptiveTeaching'))
const SpiralDetail = lazy(() => import('./pages/SpiralDetail'))
const SchoolCockpit = lazy(() => import('./pages/SchoolCockpit'))
const MyPod = lazy(() => import('./pages/MyPod'))
const PodEnroll = lazy(() => import('./pages/PodEnroll'))
const PodConsentRequest = lazy(() => import('./pages/PodConsentRequest'))
// Feature flag route wrapper
import FeatureFlagRoute from './components/FeatureFlagRoute'
// Educator-only route wrapper (coach / admin / org-staff)
import EducatorRoute from './components/EducatorRoute'

/**
 * Redirects an unauthenticated user to /login while remembering where they were headed, so
 * deep links (e.g. a coach invitation at /coach/accept-invitation/:token) survive the login
 * round-trip. saveLastPath() is written synchronously in render -- before <Navigate> commits and
 * Login mounts to consume it -- and self-filters auth/error paths.
 */
function RedirectToLogin() {
  const location = useLocation()
  saveLastPath(location.pathname + location.search)
  return <Navigate to="/login" replace />
}

function App() {
  // Use selectors to only subscribe to needed state (prevents unnecessary re-renders)
  const user = useAuthStore((state) => state.user)
  const isLoading = useAuthStore((state) => state.isLoading)
  const checkAuth = useAuthStore((state) => state.checkAuth)
  const updateUser = useAuthStore((state) => state.updateUser)
  const initializeTheme = useThemeStore((state) => state.initializeTheme)
  const forceLightMode = useThemeStore((state) => state.forceLightMode)
  const initializeBranding = useBrandingStore((state) => state.initializeBranding)
  const activeOrganizationId = useOrganizationStore((state) => state.activeOrganization?.id ?? null)
  const { enabled: darkModeEnabled, loading: darkModeLoading } = useFeatureFlag('dark_mode')
  const location = useLocation()

  // Challenge store initialization - use selector
  const initializeChallengeStore = useChallengeStore((state) => state.initializeChallengeStore)

  // Modal states
  const [showSessionExpired, setShowSessionExpired] = useState(false)
  const [showInsufficientPrivileges, setShowInsufficientPrivileges] = useState(false)
  const [sessionMessage, setSessionMessage] = useState('')
  const [sessionExpiredData, setSessionExpiredData] = useState(null)
  const [privilegesMessage, setPrivilegesMessage] = useState('')
  const [showTermsReacceptance, setShowTermsReacceptance] = useState(false)

  // What's New modal states
  const [showWhatsNew, setShowWhatsNew] = useState(false)
  const [whatsNewAnnouncements, setWhatsNewAnnouncements] = useState([])
  const [hasCheckedWhatsNew, setHasCheckedWhatsNew] = useState(false)

  // Reset What's New check when user changes (logout/login as different user)
  const currentUserId = user?.id
  useEffect(() => {
    setHasCheckedWhatsNew(false)
    setShowWhatsNew(false)
    setWhatsNewAnnouncements([])
  }, [currentUserId])

  // Track initial auth check separately from login/register loading
  const [isInitializing, setIsInitializing] = useState(true)

  // Version checking hook
  const {
    hasUpdate,
    updateInfo,
    forceUpdate,
    updatePriority,
    currentBuildId,
    newBuildId,
    performUpdate,
    dismissUpdate,
    startPolling: startVersionPolling,
    stopPolling: stopVersionPolling
  } = useVersionCheck({
    enabled: true,
    autoStart: false // We'll start polling after user is authenticated
  })

  // Initialize theme on app load, gated by dark_mode feature flag
  useEffect(() => {
    if (darkModeLoading) return
    if (darkModeEnabled) {
      initializeTheme()
    } else {
      forceLightMode()
    }
  }, [darkModeEnabled, darkModeLoading, initializeTheme, forceLightMode])

  useEffect(() => {
    const initialize = async () => {
      await checkAuth()
      setIsInitializing(false)
    }
    initialize()
  }, [checkAuth])

  // Resolve whitelabel branding on load (public; works pre-auth), whenever the
  // authenticated user changes, and whenever they switch organizations — so the
  // brand always tracks the active org and resets to default for personal mode.
  useEffect(() => {
    initializeBranding(activeOrganizationId)
  }, [initializeBranding, user?.id, activeOrganizationId])

  // Set OpenReplay user identification when user logs in/out
  useEffect(() => {
    if (user) {
      // Identify user in OpenReplay
      openReplayService.setUserID(user.email || user.id)
      openReplayService.setMetadata('userId', user.id)
      openReplayService.setMetadata('isAdmin', user.isAdmin ? 'true' : 'false')

      // Set organization if available
      if (user.organizations?.[0]?.name) {
        openReplayService.setMetadata('organization', user.organizations[0].name)
      }
    }
  }, [user])

  // Initialize version service when user is authenticated
  useEffect(() => {
    let isMounted = true

    // Import versionService for authenticated state
    import('./services/versionService').then(({ default: versionService }) => {
      // Guard against updates after unmount
      if (!isMounted) return

      if (!user || isInitializing) {
        // User logged out or initializing
        versionService.setAuthenticated(false)
        return
      }

      // User is authenticated
      versionService.setAuthenticated(true)

      // Start version polling when user is authenticated
      startVersionPolling()
    })

    return () => {
      isMounted = false
      stopVersionPolling()
    }
  }, [user, isInitializing, startVersionPolling, stopVersionPolling])

  // Check if user needs to re-accept terms after authentication
  useEffect(() => {
    if (!user || isInitializing) return

    // Skip on public routes
    const publicRoutes = [
      '/login',
      '/signup',
      '/register',
      '/register/student',
      '/register/guardian',
      '/forgot-password',
      '/reset-password',
      '/verify-email',
      '/verify',
      '/legal/terms',
      '/legal/privacy',
      '/confirm-deletion'
    ]
    if (publicRoutes.includes(location.pathname) || location.pathname.startsWith('/signup/')) {
      return
    }

    // Get current versions from environment
    const currentTermsVersion = import.meta.env.VITE_TERMS_VERSION || '1.0'
    const currentPrivacyVersion = import.meta.env.VITE_PRIVACY_VERSION || '1.0'

    logger.debug('[App] Checking terms acceptance:', {
      userTermsVersion: user.termsVersion,
      currentTermsVersion,
      userPrivacyVersion: user.privacyVersion,
      currentPrivacyVersion,
      termsAcceptedAt: user.termsAcceptedAt,
      privacyAcceptedAt: user.privacyAcceptedAt
    })

    // Check if user needs to accept terms (user version must be >= current version)
    const needsTermsAcceptance =
      !user.termsAcceptedAt ||
      parseFloat(user.termsVersion || '0') < parseFloat(currentTermsVersion)
    const needsPrivacyAcceptance =
      !user.privacyAcceptedAt ||
      parseFloat(user.privacyVersion || '0') < parseFloat(currentPrivacyVersion)

    if (needsTermsAcceptance || needsPrivacyAcceptance) {
      logger.debug('[App] User needs to re-accept terms or privacy:', {
        needsTermsAcceptance,
        needsPrivacyAcceptance
      })
      setShowTermsReacceptance(true)
    } else {
      logger.debug('[App] User terms/privacy are up to date')
      setShowTermsReacceptance(false)
    }
  }, [user, isInitializing, location.pathname])

  // Check for unseen feature announcements after login (What's New popup).
  useEffect(() => {
    if (!user || isInitializing || hasCheckedWhatsNew) return
    if (showTermsReacceptance) return

    const checkWhatsNew = async () => {
      try {
        const result = await featureAnnouncementService.getUnseen()
        if (result.announcements?.length > 0) {
          setWhatsNewAnnouncements(result.announcements)
          setShowWhatsNew(true)
        }
      } catch (err) {
        logger.error('[App] Failed to check feature announcements:', err)
      } finally {
        setHasCheckedWhatsNew(true)
      }
    }
    checkWhatsNew()
  }, [user, isInitializing, hasCheckedWhatsNew, showTermsReacceptance])

  const handleWhatsNewClose = useCallback(() => {
    setShowWhatsNew(false)
  }, [])

  const handleMarkAsRead = useCallback((ids) => {
    featureAnnouncementService
      .markAsRead(ids)
      .catch((e) => logger.error('[App] mark-read error:', e))
  }, [])

  const handleFrequencyChange = useCallback(
    async (freq) => {
      try {
        await featureAnnouncementService.updateFrequency(freq)
        updateUser({ announcementFrequency: freq })
        toast.success('Notification frequency updated')
      } catch (e) {
        logger.error('[App] Failed to update frequency:', e)
        toast.error('Failed to update frequency')
      }
    },
    [updateUser]
  )

  // ✅ APP-WIDE INITIALIZATION: Initialize challenge store on app load
  const [hasInitializedChallenges, setHasInitializedChallenges] = useState(false)
  useEffect(() => {
    if (!user || isInitializing || hasInitializedChallenges) return
    const init = async () => {
      try {
        await initializeChallengeStore()
      } catch (error) {
        logger.error('Challenge initialization error:', error)
      } finally {
        setHasInitializedChallenges(true)
      }
    }
    init()
  }, [user, isInitializing, hasInitializedChallenges, initializeChallengeStore])

  // Handle terms acceptance
  const handleTermsAccepted = (updatedUser) => {
    logger.debug('[App] Terms accepted, updating user:', updatedUser)
    logger.debug('[App] Current user before update:', user)

    // Update the user in the auth store
    updateUser(updatedUser)

    // Close the modal
    setShowTermsReacceptance(false)

    logger.debug('[App] Modal closed, user updated')
  }

  // Listen for session and privilege events
  useEffect(() => {
    const handleSessionExpired = (event) => {
      logger.debug('🚨 Session expired event received:', event.detail)
      // Close any other modals first
      setShowInsufficientPrivileges(false)
      setSessionMessage(event.detail.message)
      setShowSessionExpired(true)
      // Store additional session info for modal
      setSessionExpiredData({
        code: event.detail.code,
        isSingleSessionLogout: event.detail.isSingleSessionLogout,
        isSessionExpired: event.detail.isSessionExpired
      })
    }

    const handleInsufficientPrivileges = (event) => {
      logger.debug('🚫 Insufficient privileges event received:', event.detail)
      // Close any other modals first
      setShowSessionExpired(false)
      setPrivilegesMessage(event.detail.message)
      setShowInsufficientPrivileges(true)
    }

    // Add event listeners
    window.addEventListener('session-expired', handleSessionExpired)
    window.addEventListener('insufficient-privileges', handleInsufficientPrivileges)

    // Cleanup
    return () => {
      window.removeEventListener('session-expired', handleSessionExpired)
      window.removeEventListener('insufficient-privileges', handleInsufficientPrivileges)
    }
  }, [])

  // Log route changes
  useEffect(() => {
    // Disabled for now
    // const logData = {
    //   timestamp: new Date().toISOString(),
    //   action: 'ROUTE_CHANGE',
    //   pathname: location.pathname,
    //   search: location.search,
    //   hash: location.hash,
    //   fullPath: location.pathname + location.search + location.hash,
    //   user: user ? { id: user.id, email: user.email, isAdmin: user.isAdmin } : null,
    //   isLoading
    // }
    // console.group('🛣️ Route Change')
    // console.log('📍 New Route:', logData.fullPath)
    // console.log('👤 User:', logData.user)
    // console.log('⏳ Loading:', logData.isLoading)
    // console.log('📊 Full data:', logData)
    // console.groupEnd()
    // // Log to logging service
    // loggingService.logNavigation('', location.pathname, {
    //   action: 'ROUTE_CHANGE',
    //   search: location.search,
    //   hash: location.hash,
    //   user: logData.user,
    //   isLoading
    // })
    // // Save to localStorage for debugging
    // try {
    //   const routeHistory = JSON.parse(localStorage.getItem('routeHistory') || '[]')
    //   routeHistory.push(logData)
    //   if (routeHistory.length > 100) {
    //     routeHistory.splice(0, routeHistory.length - 100)
    //   }
    //   localStorage.setItem('routeHistory', JSON.stringify(routeHistory))
    // } catch (error) {
    //   console.warn('Failed to save route history:', error)
    // }
  }, [location, user, isLoading])

  // Only show loading spinner during initial auth check, not during login/register
  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <>
      <ImpersonationBanner />
      <ServerConnectionBanner />
      <FomoNotificationContainer />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
        <Route
          path="/signup"
          element={user ? <Navigate to="/dashboard" replace /> : <SignupLanding />}
        />
        <Route
          path="/signup/:code"
          element={user ? <Navigate to="/dashboard" replace /> : <SignupLanding />}
        />
        <Route path="/register" element={<RegisterRouter />} />
        <Route
          path="/register/student"
          element={user ? <Navigate to="/dashboard" replace /> : <RegisterStudent />}
        />
        <Route
          path="/register/guardian"
          element={user ? <Navigate to="/dashboard" replace /> : <RegisterGuardian />}
        />
        <Route
          path="/forgot-password"
          element={user ? <Navigate to="/dashboard" replace /> : <ForgotPassword />}
        />
        <Route
          path="/reset-password"
          element={user ? <Navigate to="/dashboard" replace /> : <ResetPassword />}
        />
        <Route
          path="/verify-email"
          element={user ? <Navigate to="/dashboard" replace /> : <EmailVerificationRequired />}
        />
        <Route path="/verify" element={<VerifyEmail />} />
        <Route path="/waitlist/verify" element={<WaitlistVerify />} />
        <Route path="/guardian-approval/:token" element={<GuardianApproval />} />
        <Route path="/waiver-approval/:token" element={<WaiverApproval />} />

        {/* Email invite join page - public (must be before /join/:code to match first) */}
        <Route path="/join/invite/:token" element={<JoinEmailInvite />} />
        {/* Class enrollment join page - requires auth and feature flag (lazy-loaded) */}
        <Route
          path="/join/class"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <ClassManagementGate redirect>
                <JoinClass />
              </ClassManagementGate>
            </Suspense>
          }
        />
        {/* Referral join page - public */}
        <Route path="/join/:code" element={<JoinReferral />} />

        {/* Legal pages - accessible to everyone */}
        <Route path="/legal/terms" element={<TermsOfService />} />
        <Route path="/legal/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/privacy-request" element={<PrivacyRequest />} />

        {/* Help pages - accessible to everyone */}
        <Route path="/help" element={<Help />} />
        <Route path="/help/getting-started" element={<GettingStarted />} />
        <Route path="/getting-started" element={<GettingStarted />} />
        <Route path="/contact" element={<Contact />} />

        {/* Account deletion confirmation - accessible to everyone */}
        <Route path="/confirm-deletion" element={<ConfirmDeletion />} />

        {/* Public ranking pages - accessible to everyone */}
        <Route path="/u/:publicName" element={<PublicProfile />} />
        <Route path="/u/:publicName/:strategyType" element={<PublicProfile />} />
        <Route path="/rankings/embed" element={<RankingsEmbed />} />
        <Route path="/leaderboard" element={<PublicLeaderboard />} />
        <Route path="/share/:shareId" element={<ShareLanding />} />

        {/* Protected routes. Disabled accounts get the AccountDisabled screen instead of the
            app shell (it has no <Outlet/>, so every nested route is blocked for them). `isDisabled`
            stays current without a reload: login blocks disabled users outright, and an account
            disabled mid-session is caught by the api interceptor (403 ACCOUNT_DISABLED), which flips
            this flag in the auth store. */}
        <Route
          path="/"
          element={user ? user.isDisabled ? <AccountDisabled /> : <Layout /> : <RedirectToLogin />}
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="coach" element={<CoachDashboardPage />} />
          <Route path="coach/accept-invitation/:token" element={<AcceptCoachInvitation />} />
          <Route path="challenge-type" element={<ChallengeTypeSelection />} />
          {/* Legacy V1 route - redirect to challenge type selection */}
          <Route path="challenge-session" element={<Navigate to="/challenge-type" replace />} />
          <Route
            path="challenge-session/:challengeId"
            element={
              <WebSocketErrorBoundary>
                <ChallengeSessionV2 />
              </WebSocketErrorBoundary>
            }
          />
          <Route path="challenge-complete" element={<ChallengeComplete />} />
          <Route path="challenge/:sessionId/report" element={<ChallengeReport />} />
          {/* UC-108: Email link format for challenge reports */}
          <Route path="reports/:challengeId" element={<ChallengeReport />} />
          <Route path="profile" element={<Profile />} />
          <Route path="account" element={<Navigate to="/profile#privacy" replace />} />
          <Route path="my-coach" element={<MyCoach />} />
          <Route path="history" element={<History />} />
          <Route path="whats-new" element={<WhatsNewPage />} />
          <Route path="focused-practice" element={<FocusedPracticeList />} />
          <Route path="focused-practice/:sessionId/review" element={<FocusedPracticeReview />} />
          <Route
            path="focused-practice/:sessionId/practice"
            element={<FocusedPracticePractice />}
          />
          <Route
            path="focused-practice/:sessionId/homework"
            element={<FocusedPracticeHomework />}
          />
          <Route path="focused-practice/:sessionId/results" element={<FocusedPracticeResults />} />
          <Route path="rankings" element={<Rankings />} />
          <Route path="progress" element={<Progress />} />
          <Route path="strategy/:strategyType" element={<StrategyProgress />} />
          <Route
            path="knowledge-map"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <KnowledgeMap />
              </Suspense>
            }
          />
          <Route
            path="educator/knowledge-map"
            element={
              <EducatorRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <EducatorKnowledgeMap />
                </Suspense>
              </EducatorRoute>
            }
          />
          <Route
            path="educator/teaching"
            element={
              <EducatorRoute>
                <FeatureFlagRoute flagKey="adaptive_teaching">
                  <Suspense fallback={<LoadingSpinner />}>
                    <AdaptiveTeaching />
                  </Suspense>
                </FeatureFlagRoute>
              </EducatorRoute>
            }
          />
          <Route
            path="pod"
            element={
              <EducatorRoute>
                <FeatureFlagRoute flagKey="tutor_groups">
                  <Suspense fallback={<LoadingSpinner />}>
                    <MyPod />
                  </Suspense>
                </FeatureFlagRoute>
              </EducatorRoute>
            }
          />
          {/* Student-side enrollment signing; guardian email-token signing. Both are for
              non-educators, so no EducatorRoute; the flag + server still gate everything. */}
          <Route
            path="pod/enroll/:orgId"
            element={
              <FeatureFlagRoute flagKey="tutor_groups">
                <Suspense fallback={<LoadingSpinner />}>
                  <PodEnroll />
                </Suspense>
              </FeatureFlagRoute>
            }
          />
          <Route
            path="pod-consent/:token"
            element={
              <FeatureFlagRoute flagKey="tutor_groups">
                <Suspense fallback={<LoadingSpinner />}>
                  <PodConsentRequest />
                </Suspense>
              </FeatureFlagRoute>
            }
          />
          <Route
            path="educator/cockpit"
            element={
              <EducatorRoute>
                <FeatureFlagRoute flagKey="adaptive_teaching">
                  <Suspense fallback={<LoadingSpinner />}>
                    <SchoolCockpit />
                  </Suspense>
                </FeatureFlagRoute>
              </EducatorRoute>
            }
          />
          <Route
            path="educator/teaching/:planId"
            element={
              <EducatorRoute>
                <FeatureFlagRoute flagKey="adaptive_teaching">
                  <Suspense fallback={<LoadingSpinner />}>
                    <SpiralDetail />
                  </Suspense>
                </FeatureFlagRoute>
              </EducatorRoute>
            }
          />
          <Route
            path="tutor-practice"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <TutorPracticeList />
              </Suspense>
            }
          />
          <Route
            path="tutor-practice/assigned"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <TutorAssignedPractices />
              </Suspense>
            }
          />
          <Route
            path="tutor-practice/node/:knowledgeId"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <TutorPracticeTeaching />
              </Suspense>
            }
          />
          <Route
            path="tutor-practice/:practiceId/results"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <TutorPracticeResults />
              </Suspense>
            }
          />
          <Route
            path="tutor-practice/:practiceId"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <TutorPracticeTeaching />
              </Suspense>
            }
          />
          <Route
            path="score-reports"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <ScoreReports />
              </Suspense>
            }
          />
          <Route
            path="score-reports/:reportId"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <ScoreReportDetailPage />
              </Suspense>
            }
          />
          <Route
            path="my-report"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <MyReport />
              </Suspense>
            }
          />
          <Route path="my-feedback" element={<MyFeedback />} />
          <Route path="my-feedback/:feedbackId" element={<FeedbackDetail />} />
          <Route path="guardian/child/:childId" element={<ChildDetailPage />} />
          <Route path="coach/client/:clientId" element={<ClientDetailPage />} />
          <Route path="games" element={<Games />} />
          <Route path="games/math-matching" element={<MathMatchingGame />} />
          <Route path="games/speed-vocab" element={<SpeedVocab />} />
          <Route path="games/grammar-fix" element={<GrammarFix />} />
          <Route path="games/context-clues" element={<ContextClues />} />
          <Route path="referrals" element={<Referrals />} />
          <Route path="messages" element={<Messages />} />
          <Route path="messages/:conversationId" element={<Messages />} />
          <Route path="checkout" element={<CheckoutPage />} />
          <Route
            path="calculator"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <FeatureFlagRoute flagKey="graphing_calculator">
                  <CalculatorPage />
                </FeatureFlagRoute>
              </Suspense>
            }
          />
          <Route path="admin" element={<AdminDashboard />} />
          {/* Marketing moved under Admin → keep the old URL working as a redirect */}
          <Route path="marketing" element={<Navigate to="/admin#marketing" replace />} />
          <Route
            path="calendar"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <CalendarPage />
              </Suspense>
            }
          />
          <Route
            path="events/:occurrenceId"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <EventDetailPage />
              </Suspense>
            }
          />
          {/* Class Management Routes (Coach/Instructor) - lazy-loaded with feature flag */}
          <Route
            path="coach/classes"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <ClassManagementGate>
                  <ClassList />
                </ClassManagementGate>
              </Suspense>
            }
          />
          <Route
            path="coach/classes/:classId"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <ClassManagementGate>
                  <ClassDetail />
                </ClassManagementGate>
              </Suspense>
            }
          />
        </Route>

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>

      {/* Session and Privilege Modals */}
      <SessionExpiredModal
        isOpen={showSessionExpired}
        onClose={() => {
          setShowSessionExpired(false)
          setSessionExpiredData(null)
        }}
        message={sessionMessage}
        code={sessionExpiredData?.code}
        isSingleSessionLogout={sessionExpiredData?.isSingleSessionLogout}
      />

      <InsufficientPrivilegesModal
        isOpen={showInsufficientPrivileges}
        onClose={() => setShowInsufficientPrivileges(false)}
        message={privilegesMessage}
      />

      {/* Feedback Modals */}
      <FeedbackModal />
      {user?.isAdmin ? <AdminFeedbackDetailModal /> : <FeedbackDetailModal />}

      {/* Terms Re-acceptance Modal */}
      <TermsReacceptanceModal isOpen={showTermsReacceptance} onAccepted={handleTermsAccepted} />

      {/* What's New Modal */}
      <ModalErrorBoundary name="WhatsNewModal">
        <WhatsNewModal
          isOpen={showWhatsNew && !showTermsReacceptance}
          onClose={handleWhatsNewClose}
          announcements={whatsNewAnnouncements}
          onMarkAsRead={handleMarkAsRead}
          userFrequency={user?.announcementFrequency || 'every_login'}
          onFrequencyChange={handleFrequencyChange}
        />
      </ModalErrorBoundary>

      {/* Version Update Modal */}
      <ModalErrorBoundary name="UpdateAvailableModal">
        <UpdateAvailableModal
          isOpen={hasUpdate}
          onUpdate={performUpdate}
          onDismiss={forceUpdate ? undefined : dismissUpdate}
          currentBuildId={currentBuildId}
          newBuildId={newBuildId}
          updatePriority={updatePriority}
          changelog={updateInfo?.changelog}
        />
      </ModalErrorBoundary>
    </>
  )
}

export default App
